import { defineComponent as _defineComponent } from 'vue'
import { toDisplayString as _toDisplayString, openBlock as _openBlock, createElementBlock as _createElementBlock, createCommentVNode as _createCommentVNode, renderList as _renderList, Fragment as _Fragment, createElementVNode as _createElementVNode, unref as _unref, withCtx as _withCtx, createBlock as _createBlock, normalizeClass as _normalizeClass, withModifiers as _withModifiers, normalizeStyle as _normalizeStyle } from "vue"

const _hoisted_1 = { class: "ncr-messages-pane" }
const _hoisted_2 = {
  key: 0,
  class: "ncr-pane-alert is-error"
}
const _hoisted_3 = {
  key: 1,
  class: "ncr-pane-alert is-notice"
}
const _hoisted_4 = {
  key: 2,
  class: "ncr-pane-empty"
}
const _hoisted_5 = {
  key: 3,
  class: "ncr-pane-empty"
}
const _hoisted_6 = {
  key: 4,
  class: "ncr-messages-list scroll-content"
}
const _hoisted_7 = ["src"]
const _hoisted_8 = { class: "ncr-message-copy" }
const _hoisted_9 = { class: "ncr-message-author-line" }
const _hoisted_10 = {
  key: 0,
  class: "ncr-message-legacy"
}
const _hoisted_11 = {
  key: 1,
  class: "ncr-message-edited"
}
const _hoisted_12 = ["innerHTML"]
const _hoisted_13 = {
  key: 0,
  class: "ncr-message-attachments"
}
const _hoisted_14 = ["title", "onClick"]
const _hoisted_15 = ["src", "alt"]
const _hoisted_16 = { key: 1 }
const _hoisted_17 = ["src", "alt"]
const _hoisted_18 = {
  key: 1,
  class: "composer-attachment-label"
}
const _hoisted_19 = { class: "composer-attachment-name" }
const _hoisted_20 = ["aria-label", "onClick"]
const _hoisted_21 = {
  key: 0,
  class: "composer-attachment-card uploading is-file"
}
const _hoisted_22 = { class: "composer-attachment-label" }
const _hoisted_23 = {
  class: "composer-attachment-progress-shell",
  "aria-hidden": "true"
}
const _hoisted_24 = {
  key: 0,
  class: "composer-suggestion-list",
  role: "listbox",
  "aria-label": "Message suggestions"
}
const _hoisted_25 = ["onMouseenter", "onMousedown"]
const _hoisted_26 = ["src"]
const _hoisted_27 = {
  key: 1,
  class: "composer-suggestion-hash"
}
const _hoisted_28 = { class: "composer-suggestion-line" }
const _hoisted_29 = { class: "composer-suggestion-label" }
const _hoisted_30 = {
  key: 0,
  class: "composer-suggestion-separator"
}
const _hoisted_31 = {
  key: 1,
  class: "composer-suggestion-secondary"
}

import { computed, nextTick, onBeforeUnmount, ref, watch } from 'vue';
import {
  collection,
  documentId,
  endAt,
  getDocs,
  limit,
  orderBy,
  query,
  startAt,
  type Unsubscribe
} from 'firebase/firestore';

import {
  MessageComposer,
  OverflowMenu,
  type MessageComposerExpose,
  type OverflowMenuOption
} from '@/components/ui';
import { fileClient, type RecordFile } from '@/modules/common/files/FileClient';
import {
  findDuplicateRecordFileUpload,
  getRecordFilePreviewKind,
  getRecordFilePreviewLabel,
  uploadRecordFiles,
  validateLocalRecordFile
} from '@/modules/common/files/RecordFileWorkflow';
import {
  messageClient,
  type MessageAttachment,
  type MessageEntityCandidates,
  type MessageMentionCandidate,
  type MessageNcrReferenceCandidate,
  type MessageRecord
} from '@/modules/common/messaging/MessageClient';
import { subscribeMessageFeed } from '@/modules/common/messaging/MessageFeed';
import { markSubjectNotificationsRead } from '@/modules/notifications/NotificationClient';
import { db } from '@/modules/firebase';
import { getCurrentUser } from '@/modules/firebase/Auth';
import { ncrClient } from '@/modules/ncr/NcrClient';
import { UserStore } from '@/modules/users/UserStore';

type ComposerTokenKind = 'mention' | 'ncr';

interface ComposerTokenContext {
  kind: ComposerTokenKind;
  query: string;
  start: number;
  end: number;
}

interface MentionSuggestion {
  kind: 'mention';
  key: string;
  uid: string;
  label: string;
  secondary: string;
  avatar: string;
}

interface NcrSuggestion {
  kind: 'ncr';
  key: string;
  ncrId: string;
  label: string;
  secondary: string;
}

type ComposerSuggestion = MentionSuggestion | NcrSuggestion;


export default /*@__PURE__*/_defineComponent({
  __name: 'NcrMessagesPane',
  props: {
    recordId: {},
    readonly: { type: Boolean }
  },
  setup(__props: any) {

const props = __props;

const messages = ref<MessageRecord[]>([]);
const draft = ref('');
const draftAttachments = ref<MessageAttachment[]>([]);
const selectedEntities = ref<MessageEntityCandidates>({ mentions: [], ncrs: [] });
const composerSuggestions = ref<ComposerSuggestion[]>([]);
const highlightedSuggestionIndex = ref(0);
const forcedSuggestionKind = ref<ComposerTokenKind | null>(null);
const isLoading = ref(false);
const isSending = ref(false);
const isUploadingAttachments = ref(false);
const editingMessageId = ref('');
const deletingMessageId = ref('');
const messageMenuRenderKey = ref(0);
const uploadProgress = ref(0);
const uploadLabel = ref('Uploading');
const errorMessage = ref('');
const noticeMessage = ref('');
const attachmentPreviewUrls = ref<Record<string, string>>({});
const composerRef = ref<MessageComposerExpose | null>(null);
const attachmentInputRef = ref<HTMLInputElement | null>(null);
let unsubscribes: Unsubscribe[] = [];
let loadGeneration = 0;
let suggestionGeneration = 0;

const currentUserAvatar = computed(() => {
  const user = getCurrentUser();
  return user?.slack?.avatar
    || user?.photoURL
    || UserStore.getAvatar(user?.uid || user?.email || user?.displayName)
    || '/assets/img/avatar.png';
});

const canAttach = computed(() => Boolean(props.recordId) && !isSending.value && !isUploadingAttachments.value
  && !editingMessageId.value && draftAttachments.value.length < 3);

const isEditing = computed(() => Boolean(editingMessageId.value));

const canSend = computed(() => Boolean(props.recordId)
  && !isSending.value
  && !isUploadingAttachments.value
  && (draft.value.trim().length > 0 || draftAttachments.value.length > 0));

const composerHighlightHtml = computed(() => formatComposerHtml(draft.value));

function escapeHtml(value: string): string {
  return value
    .replace(/&/g, '&amp;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;')
    .replace(/"/g, '&quot;')
    .replace(/'/g, '&#39;');
}

function escapeAttribute(value: string): string {
  return escapeHtml(value).replace(/`/g, '&#96;');
}

function plainHtml(value: string): string {
  return escapeHtml(value).replace(/\n/g, '<br>');
}

function getComposerElement(): HTMLTextAreaElement | null {
  return composerRef.value?.getTextareaElement() || null;
}

function formatComposerHtml(value: string): string {
  if (!value) return '';

  const labels = [
    ...(selectedEntities.value.mentions || []).map(entity => ({ label: entity.label || '', kind: 'mention' })),
    ...(selectedEntities.value.ncrs || []).map(entity => ({ label: entity.label || '', kind: 'ncr' }))
  ].filter(entity => entity.label && value.includes(entity.label));

  if (!labels.length) {
    return plainHtml(value);
  }

  const ranges = labels
    .map(entity => ({ ...entity, start: value.indexOf(entity.label), end: value.indexOf(entity.label) + entity.label.length }))
    .sort((left, right) => left.start - right.start);
  let cursor = 0;
  let html = '';

  for (const range of ranges) {
    if (range.start < cursor) continue;
    html += plainHtml(value.slice(cursor, range.start));
    html += `<span class="composer-entity composer-entity-${range.kind}">${plainHtml(value.slice(range.start, range.end))}</span>`;
    cursor = range.end;
  }

  return html + plainHtml(value.slice(cursor));
}

function formatMessageHtml(message: MessageRecord): string {
  const text = message.text || message.lines.join('\n');
  const entities = [
    ...(message.entities?.mentions || []).map(entity => ({ ...entity, kind: 'mention' as const })),
    ...(message.entities?.ncrs || []).map(entity => ({ ...entity, kind: 'ncr' as const }))
  ]
    .filter(entity => Number.isInteger(entity.start) && Number.isInteger(entity.end))
    .sort((left, right) => Number(left.start) - Number(right.start));

  if (!entities.length) return plainHtml(text);

  let cursor = 0;
  let html = '';
  for (const entity of entities) {
    const start = Number(entity.start);
    const end = Number(entity.end);
    if (start < cursor || end <= start || end > text.length) continue;

    html += plainHtml(text.slice(cursor, start));
    const entityText = plainHtml(text.slice(start, end));
    if (entity.kind === 'mention') {
      html += `<span class="ncr-message-entity is-mention" data-uid="${escapeAttribute(entity.uid)}">${entityText}</span>`;
    } else {
      html += `<a class="ncr-message-entity is-ncr" href="/ncr2/${encodeURIComponent(entity.ncrId)}" data-ncr-id="${escapeAttribute(entity.ncrId)}">${entityText}</a>`;
    }
    cursor = end;
  }

  return html + plainHtml(text.slice(cursor));
}

function handleMessageContentClick(event: MouseEvent): void {
  const target = event.target instanceof Element ? event.target.closest<HTMLElement>('[data-ncr-id]') : null;
  const ncrId = target?.dataset.ncrId;
  if (!ncrId) return;

  event.preventDefault();
  void window.NcrModalController?.edit(ncrId);
}

function stopSubscription(): void {
  loadGeneration += 1;
  unsubscribes.forEach(unsubscribe => unsubscribe());
  unsubscribes = [];
}

function startSubscription(recordId: string): void {
  stopSubscription();
  resetComposer();
  messages.value = [];
  errorMessage.value = '';
  isLoading.value = Boolean(recordId);
  void UserStore.init();
  if (!recordId) return;
  void markSubjectNotificationsRead('ncr', recordId).catch(error => {
    console.warn('[NcrMessagesPane] Unable to reconcile notification read state:', error);
  });

  const generation = loadGeneration;
  const isCurrent = () => generation === loadGeneration && recordId === props.recordId;
  const user = getCurrentUser();
  unsubscribes = [subscribeMessageFeed(
    'ncr', recordId, {
      uid: user?.uid || '',
      isAdmin: user?.permissions?.adminAccess === true
    },
    nextMessages => {
      if (!isCurrent()) return;
      messages.value = nextMessages.sort((left, right) => left.timestampMs - right.timestampMs);
      isLoading.value = false;
      nextMessages.flatMap(message => message.attachments || []).forEach(attachment => {
        void ensureAttachmentPreview(attachment);
      });
    },
    error => {
      if (!isCurrent()) return;
      errorMessage.value = error.message || 'Failed to load messages';
      isLoading.value = false;
    },
    100
  )];
}

async function sendMessage(): Promise<void> {
  if (!canSend.value) return;

  const text = draft.value.trim();
  const draftSnapshot = draft.value;
  const attachmentSnapshot = [...draftAttachments.value];
  const entitiesSnapshot: MessageEntityCandidates = {
    mentions: [...(selectedEntities.value.mentions || [])],
    ncrs: [...(selectedEntities.value.ncrs || [])]
  };
  const targetMessageId = editingMessageId.value;

  isSending.value = true;
  errorMessage.value = '';
  noticeMessage.value = '';
  try {
    const entityCandidates = buildEntityCandidates(text);
    const attachmentCandidates = draftAttachments.value.map(attachment => ({ fileId: attachment.fileId }));
    const savedMessage = targetMessageId
      ? await messageClient.updateMessage(
        'ncr',
        props.recordId,
        targetMessageId,
        text,
        entityCandidates,
        attachmentCandidates
      )
      : await messageClient.createMessage(
        'ncr',
        props.recordId,
        text,
        entityCandidates,
        attachmentCandidates
      );
    resetComposer();
    noticeMessage.value = (savedMessage.warnings || []).map(warning => warning.message).join(' ');
  } catch (error) {
    draft.value = draftSnapshot;
    draftAttachments.value = attachmentSnapshot;
    selectedEntities.value = entitiesSnapshot;
    errorMessage.value = error instanceof Error ? error.message : 'Failed to send message';
  } finally {
    isSending.value = false;
  }
}

async function removeMessage(message: MessageRecord): Promise<void> {
  if (!message.actionState?.canDelete || deletingMessageId.value === message.id) return;
  if (!window.confirm('Delete this message?')) return;

  deletingMessageId.value = message.id;
  closeMessageMenu();
  try {
    await messageClient.deleteMessage('ncr', props.recordId, message.id);
  } catch (error) {
    errorMessage.value = error instanceof Error ? error.message : 'Failed to delete message';
  } finally {
    deletingMessageId.value = '';
  }
}

function canShowMessageActions(message: MessageRecord): boolean {
  return !props.readonly && (message.actionState?.canEdit === true || message.actionState?.canDelete === true);
}

function getMessageMenuOptions(message: MessageRecord): OverflowMenuOption[] {
  return [
    {
      key: 'edit',
      label: 'Edit',
      icon: 'edit',
      disabled: message.actionState?.canEdit !== true || isSending.value
    },
    {
      key: 'delete',
      label: 'Delete',
      icon: 'delete',
      tone: 'danger',
      disabled: message.actionState?.canDelete !== true || deletingMessageId.value === message.id
    }
  ];
}

function handleMessageMenuSelect(actionKey: string, message: MessageRecord): void {
  if (actionKey === 'edit') {
    startEditingMessage(message);
  } else if (actionKey === 'delete') {
    void removeMessage(message);
  }
}

function startEditingMessage(message: MessageRecord): void {
  if (message.actionState?.canEdit !== true || isSending.value) return;

  closeMessageMenu();
  editingMessageId.value = message.id;
  draft.value = message.text;
  draftAttachments.value = [...(message.attachments || [])];
  selectedEntities.value = {
    mentions: (message.entities?.mentions || []).map(entity => ({
      uid: entity.uid,
      label: entity.label,
      start: entity.start ?? undefined,
      end: entity.end ?? undefined
    })),
    ncrs: (message.entities?.ncrs || []).map(entity => ({
      ncrId: entity.ncrId,
      label: entity.label,
      start: entity.start ?? undefined,
      end: entity.end ?? undefined
    }))
  };
  errorMessage.value = '';
  void nextTick(() => {
    composerRef.value?.focus();
    const end = draft.value.length;
    composerRef.value?.setSelectionRange(end, end);
  });
}

function cancelEditingMessage(): void {
  resetComposer();
  errorMessage.value = '';
}

function closeMessageMenu(): void {
  messageMenuRenderKey.value += 1;
}

function resetComposer(): void {
  draft.value = '';
  draftAttachments.value = [];
  selectedEntities.value = { mentions: [], ncrs: [] };
  composerSuggestions.value = [];
  highlightedSuggestionIndex.value = 0;
  forcedSuggestionKind.value = null;
  editingMessageId.value = '';
}

function openAttachmentPicker(): void {
  if (canAttach.value) attachmentInputRef.value?.click();
}

async function handleAttachmentInputChange(event: Event): Promise<void> {
  const input = event.target as HTMLInputElement;
  const files = Array.from(input.files || []);
  input.value = '';
  if (!files.length) return;

  const remainingSlots = 3 - draftAttachments.value.length;
  if (files.length > remainingSlots) {
    noticeMessage.value = `A message can include up to 3 files. You can add ${remainingSlots} more.`;
    return;
  }

  const validationError = files.map(validateLocalRecordFile).find(Boolean);
  if (validationError) {
    errorMessage.value = validationError;
    return;
  }

  const duplicate = findDuplicateRecordFileUpload(files, draftAttachments.value);
  if (duplicate) {
    noticeMessage.value = `${duplicate} is already attached to this draft.`;
    return;
  }

  isUploadingAttachments.value = true;
  uploadProgress.value = 0;
  uploadLabel.value = files.length === 1 ? files[0].name : `${files.length} files`;
  errorMessage.value = '';
  try {
    const uploadedFiles = await uploadRecordFiles({
      recordType: 'ncr',
      recordId: props.recordId,
      files,
      onProgress: progress => {
        uploadProgress.value = progress.percent;
      }
    });

    for (const file of uploadedFiles) {
      const attachment = toMessageAttachment(file);
      draftAttachments.value.push(attachment);
      await ensureAttachmentPreview(attachment);
      const downloadUrl = await fileClient.getDownloadUrl(file.storagePath);
      await ncrClient.addModernFileMirror(props.recordId, file, downloadUrl);
    }
  } catch (error) {
    errorMessage.value = error instanceof Error ? error.message : 'Failed to upload attachment';
  } finally {
    isUploadingAttachments.value = false;
    uploadProgress.value = 0;
  }
}

function toMessageAttachment(file: RecordFile): MessageAttachment {
  return {
    fileId: file.id,
    displayName: file.displayName,
    originalName: file.originalName,
    mimeType: file.mimeType,
    extension: file.extension,
    sizeBytes: file.sizeBytes,
    storagePath: file.storagePath,
    version: file.version,
    previewKind: getRecordFilePreviewKind(file)
  };
}

function removeDraftAttachment(fileId: string): void {
  draftAttachments.value = draftAttachments.value.filter(attachment => attachment.fileId !== fileId);
}

function attachmentLabel(attachment: MessageAttachment): string {
  return getRecordFilePreviewLabel(attachment);
}

async function ensureAttachmentPreview(attachment: MessageAttachment): Promise<void> {
  if (attachment.previewKind !== 'image' || attachmentPreviewUrls.value[attachment.fileId]) return;
  try {
    const url = await fileClient.getDownloadUrl(attachment.storagePath);
    attachmentPreviewUrls.value = { ...attachmentPreviewUrls.value, [attachment.fileId]: url };
  } catch (error) {
    console.warn('[NcrMessagesPane] Attachment preview unavailable:', error);
  }
}

async function openAttachment(attachment: MessageAttachment): Promise<void> {
  try {
    const url = await fileClient.getDownloadUrl(attachment.storagePath);
    window.open(url, '_blank', 'noopener');
  } catch (error) {
    errorMessage.value = error instanceof Error ? error.message : 'Unable to open attachment';
  }
}

async function insertComposerTokenAndBrowse(kind: ComposerTokenKind, token: string): Promise<void> {
  const textarea = getComposerElement();
  const caret = textarea?.selectionStart ?? draft.value.length;
  const existing = extractTokenContext(draft.value, caret);
  if (existing?.kind === kind) {
    forcedSuggestionKind.value = kind;
    await updateComposerSuggestions();
    composerRef.value?.focus();
    return;
  }

  const before = draft.value.slice(0, caret);
  const after = draft.value.slice(caret);
  const insertion = `${before && !/\s$/.test(before) ? ' ' : ''}${token}`;
  draft.value = `${before}${insertion}${after}`;
  const nextCaret = caret + insertion.length;
  forcedSuggestionKind.value = kind;

  await nextTick();
  composerRef.value?.focus();
  composerRef.value?.setSelectionRange(nextCaret, nextCaret);
  await updateComposerSuggestions();
}

function extractTokenContext(text: string, caret: number): ComposerTokenContext | null {
  const beforeCaret = text.slice(0, caret);
  const match = beforeCaret.match(/(^|[\s\n])([@#][^\s@#]*)$/);
  if (!match) return null;

  const token = match[2];
  return {
    kind: token.startsWith('@') ? 'mention' : 'ncr',
    query: token.slice(1),
    start: beforeCaret.length - token.length,
    end: beforeCaret.length
  };
}

async function updateComposerSuggestions(): Promise<void> {
  const textarea = getComposerElement();
  const token = textarea ? extractTokenContext(draft.value, textarea.selectionStart ?? draft.value.length) : null;
  if (!token) {
    composerSuggestions.value = [];
    forcedSuggestionKind.value = null;
    pruneSelectedEntities();
    return;
  }

  const generation = ++suggestionGeneration;
  if (token.kind === 'mention') {
    const queryValue = token.query.trim();
    if (queryValue.length < 2 && forcedSuggestionKind.value !== 'mention') {
      composerSuggestions.value = [];
      return;
    }

    const users = forcedSuggestionKind.value === 'mention' && !queryValue
      ? Object.values(UserStore.getAllUsers()).filter(user => Boolean(user.uid))
      : UserStore.searchUsers(queryValue);
    const nextSuggestions: ComposerSuggestion[] = users
      .slice()
      .sort((left, right) => left.display_name.localeCompare(right.display_name))
      .slice(0, 20)
      .map(user => ({
        kind: 'mention',
        key: `mention-${user.uid}`,
        uid: user.uid as string,
        label: user.display_name.startsWith('@') ? user.display_name : `@${user.display_name}`,
        secondary: user.email || '',
        avatar: user.avatar
      }));
    if (generation !== suggestionGeneration) return;
    composerSuggestions.value = nextSuggestions;
  } else {
    try {
      const nextSuggestions = await loadNcrSuggestions(token.query, forcedSuggestionKind.value === 'ncr');
      if (generation !== suggestionGeneration) return;
      composerSuggestions.value = nextSuggestions;
    } catch (error) {
      if (generation !== suggestionGeneration) return;
      composerSuggestions.value = [];
      errorMessage.value = error instanceof Error ? error.message : 'Unable to load NCR suggestions';
    }
  }

  highlightedSuggestionIndex.value = 0;
}

async function loadNcrSuggestions(searchValue: string, showFullList: boolean): Promise<NcrSuggestion[]> {
  const trimmed = searchValue.trim().toUpperCase();
  const prefix = trimmed && !trimmed.startsWith('NCR-')
    ? `NCR-${trimmed.replace(/^NCR/, '').replace(/^-/, '')}`
    : trimmed;

  if (!prefix && !showFullList) return [];

  const ncrQuery = prefix
    ? query(collection(db, 'ncr'), orderBy(documentId()), startAt(prefix), endAt(`${prefix}\uf8ff`), limit(20))
    : query(collection(db, 'ncr'), orderBy('datetime', 'desc'), limit(25));
  const snapshot = await getDocs(ncrQuery);

  return snapshot.docs
    .filter(entry => entry.id !== props.recordId)
    .map(entry => {
      const data = entry.data();
      return {
        kind: 'ncr',
        key: `ncr-${entry.id}`,
        ncrId: entry.id,
        label: `#${entry.id}`,
        secondary: [data.newpart || data.partno, data.status].filter(Boolean).join(' • ')
      };
    });
}

function applyComposerSuggestion(index: number): void {
  const suggestion = composerSuggestions.value[index];
  const textarea = getComposerElement();
  if (!suggestion || !textarea) return;

  const token = extractTokenContext(draft.value, textarea.selectionStart ?? draft.value.length);
  if (!token) return;

  const insertion = `${suggestion.label} `;
  draft.value = `${draft.value.slice(0, token.start)}${insertion}${draft.value.slice(token.end)}`;
  if (suggestion.kind === 'mention') {
    selectedEntities.value.mentions = [
      ...(selectedEntities.value.mentions || []).filter(entity => entity.uid !== suggestion.uid),
      { uid: suggestion.uid, label: suggestion.label }
    ];
  } else {
    selectedEntities.value.ncrs = [
      ...(selectedEntities.value.ncrs || []).filter(entity => entity.ncrId !== suggestion.ncrId),
      { ncrId: suggestion.ncrId, label: suggestion.label }
    ];
  }

  composerSuggestions.value = [];
  forcedSuggestionKind.value = null;
  const nextCaret = token.start + insertion.length;
  void nextTick(() => {
    composerRef.value?.focus();
    composerRef.value?.setSelectionRange(nextCaret, nextCaret);
  });
}

function pruneSelectedEntities(): void {
  selectedEntities.value = {
    mentions: (selectedEntities.value.mentions || []).filter(entity => entity.label && draft.value.includes(entity.label)),
    ncrs: (selectedEntities.value.ncrs || []).filter(entity => entity.label && draft.value.includes(entity.label))
  };
}

function buildEntityCandidates(text: string): MessageEntityCandidates | undefined {
  pruneSelectedEntities();
  const mentions = (selectedEntities.value.mentions || []).map(entity => withTextRange(entity, text));
  const ncrs = (selectedEntities.value.ncrs || []).map(entity => withTextRange(entity, text));

  const knownMentionUids = new Set(mentions.map(entity => entity.uid));
  const aliases = new Map<string, string>();
  UserStore.getAllProfiles().forEach(profile => {
    [profile.slack?.display_name, profile.slack?.real_name, profile.displayName, profile.email?.split('@')[0]]
      .filter((alias): alias is string => Boolean(alias?.trim()))
      .forEach(alias => aliases.set(alias.trim().toLowerCase(), profile.uid));
  });

  for (const match of text.matchAll(/(^|[\s\n])@([^\s@#]+)/g)) {
    const uid = aliases.get(match[2].toLowerCase());
    if (!uid || knownMentionUids.has(uid)) continue;
    const start = (match.index || 0) + match[1].length;
    const label = text.slice(start, start + match[0].trim().length);
    mentions.push({ uid, label, start, end: start + label.length });
    knownMentionUids.add(uid);
  }

  const knownNcrs = new Set(ncrs.map(entity => entity.ncrId));
  for (const match of text.matchAll(/#(NCR-\d+)/gi)) {
    const ncrId = match[1].toUpperCase();
    if (knownNcrs.has(ncrId)) continue;
    ncrs.push({ ncrId, label: match[0], start: match.index, end: (match.index || 0) + match[0].length });
    knownNcrs.add(ncrId);
  }

  return mentions.length || ncrs.length ? { mentions, ncrs } : undefined;
}

function withTextRange<T extends MessageMentionCandidate | MessageNcrReferenceCandidate>(entity: T, text: string): T {
  const label = entity.label || '';
  const start = text.indexOf(label);
  return { ...entity, start, end: start >= 0 ? start + label.length : start };
}

function handleComposerKeydown(event: KeyboardEvent): void {
  if (composerSuggestions.value.length) {
    if (event.key === 'ArrowDown') {
      event.preventDefault();
      highlightedSuggestionIndex.value = (highlightedSuggestionIndex.value + 1) % composerSuggestions.value.length;
      return;
    }
    if (event.key === 'ArrowUp') {
      event.preventDefault();
      highlightedSuggestionIndex.value = (highlightedSuggestionIndex.value - 1 + composerSuggestions.value.length)
        % composerSuggestions.value.length;
      return;
    }
    if ((event.key === 'Enter' || event.key === 'Tab') && composerSuggestions.value[highlightedSuggestionIndex.value]) {
      event.preventDefault();
      applyComposerSuggestion(highlightedSuggestionIndex.value);
      return;
    }
    if (event.key === 'Escape') {
      event.preventDefault();
      composerSuggestions.value = [];
      forcedSuggestionKind.value = null;
      return;
    }
  }

  if (event.key === 'Enter' && !event.shiftKey) {
    event.preventDefault();
    void sendMessage();
  }
}

function authorFor(authorId: string): string {
  const user = UserStore.getUserByUid(authorId) || UserStore.getUserByName(authorId);
  return user?.display_name || authorId || 'Unknown user';
}

function avatarFor(authorId: string): string {
  const user = UserStore.getUserByUid(authorId) || UserStore.getUserByName(authorId);
  return user?.avatar || '/assets/img/avatar.png';
}

function formatDate(timestampMs: number): string {
  return timestampMs ? new Date(timestampMs).toLocaleString() : '';
}

watch(() => props.recordId, startSubscription, { immediate: true });
onBeforeUnmount(stopSubscription);

return (_ctx: any,_cache: any) => {
  return (_openBlock(), _createElementBlock("section", _hoisted_1, [
    (errorMessage.value)
      ? (_openBlock(), _createElementBlock("div", _hoisted_2, _toDisplayString(errorMessage.value), 1 /* TEXT */))
      : _createCommentVNode("v-if", true),
    (noticeMessage.value)
      ? (_openBlock(), _createElementBlock("div", _hoisted_3, _toDisplayString(noticeMessage.value), 1 /* TEXT */))
      : _createCommentVNode("v-if", true),
    (isLoading.value)
      ? (_openBlock(), _createElementBlock("div", _hoisted_4, "Loading messages…"))
      : (messages.value.length === 0)
        ? (_openBlock(), _createElementBlock("div", _hoisted_5, "No messages yet."))
        : (_openBlock(), _createElementBlock("div", _hoisted_6, [
            (_openBlock(true), _createElementBlock(_Fragment, null, _renderList(messages.value, (message) => {
              return (_openBlock(), _createElementBlock("article", {
                key: `${message.source}-${message.id}`,
                class: "ncr-message"
              }, [
                _createElementVNode("img", {
                  src: avatarFor(message.authorId),
                  alt: ""
                }, null, 8 /* PROPS */, _hoisted_7),
                _createElementVNode("div", _hoisted_8, [
                  _createElementVNode("header", null, [
                    _createElementVNode("div", _hoisted_9, [
                      _createElementVNode("strong", null, _toDisplayString(authorFor(message.authorId)), 1 /* TEXT */),
                      _createElementVNode("time", null, _toDisplayString(formatDate(message.timestampMs)), 1 /* TEXT */),
                      (message.legacy)
                        ? (_openBlock(), _createElementBlock("span", _hoisted_10, "Legacy note"))
                        : _createCommentVNode("v-if", true),
                      (message.edited)
                        ? (_openBlock(), _createElementBlock("span", _hoisted_11, "Edited"))
                        : _createCommentVNode("v-if", true)
                    ]),
                    (canShowMessageActions(message))
                      ? (_openBlock(), _createBlock(_unref(OverflowMenu), {
                          key: `${message.id}-${messageMenuRenderKey.value}`,
                          class: "ncr-message-action-menu",
                          "button-label": "Message actions",
                          portal: true,
                          placement: "top",
                          "trigger-class": ['ncr-message-action-trigger'],
                          options: getMessageMenuOptions(message),
                          onSelect: ($event: any) => (handleMessageMenuSelect($event, message))
                        }, {
                          trigger: _withCtx(() => [...(_cache[3] || (_cache[3] = [
                            _createElementVNode("i", { class: "material-icons" }, "more_vert", -1 /* CACHED */)
                          ]))]),
                          _: 1 /* STABLE */
                        }, 8 /* PROPS */, ["options", "onSelect"]))
                      : _createCommentVNode("v-if", true)
                  ]),
                  _createElementVNode("p", {
                    class: "ncr-message-body",
                    onClick: handleMessageContentClick,
                    innerHTML: formatMessageHtml(message)
                  }, null, 8 /* PROPS */, _hoisted_12),
                  (message.attachments?.length)
                    ? (_openBlock(), _createElementBlock("div", _hoisted_13, [
                        (_openBlock(true), _createElementBlock(_Fragment, null, _renderList(message.attachments, (attachment) => {
                          return (_openBlock(), _createElementBlock("button", {
                            key: `${message.id}-${attachment.fileId}`,
                            type: "button",
                            class: _normalizeClass(['ncr-message-attachment', `is-${attachment.previewKind}`]),
                            title: attachment.displayName,
                            onClick: ($event: any) => (openAttachment(attachment))
                          }, [
                            (attachmentPreviewUrls.value[attachment.fileId])
                              ? (_openBlock(), _createElementBlock("img", {
                                  key: 0,
                                  src: attachmentPreviewUrls.value[attachment.fileId],
                                  alt: `${attachment.displayName} preview`,
                                  loading: "lazy"
                                }, null, 8 /* PROPS */, _hoisted_15))
                              : (_openBlock(), _createElementBlock("span", _hoisted_16, _toDisplayString(attachmentLabel(attachment)), 1 /* TEXT */)),
                            _createElementVNode("small", null, _toDisplayString(attachment.displayName), 1 /* TEXT */)
                          ], 10 /* CLASS, PROPS */, _hoisted_14))
                        }), 128 /* KEYED_FRAGMENT */))
                      ]))
                    : _createCommentVNode("v-if", true)
                ])
              ]))
            }), 128 /* KEYED_FRAGMENT */))
          ])),
    (!__props.readonly)
      ? (_openBlock(), _createBlock(_unref(MessageComposer), {
          key: 5,
          ref_key: "composerRef",
          ref: composerRef,
          modelValue: draft.value,
          "onUpdate:modelValue": _cache[0] || (_cache[0] = ($event: any) => ((draft).value = $event)),
          class: "ncr-message-composer",
          placeholder: "Log notes, decisions, or analysis",
          "avatar-url": currentUserAvatar.value,
          sending: isSending.value,
          "send-disabled": !canSend.value,
          "can-attach": canAttach.value,
          "has-attachments": draftAttachments.value.length > 0,
          "force-expanded": composerSuggestions.value.length > 0,
          "highlight-html": composerHighlightHtml.value,
          "is-editing": isEditing.value,
          "link-aria-label": "Link another NCR",
          "link-tooltip": "Link NCR",
          onSubmit: sendMessage,
          onCancel: cancelEditingMessage,
          onMention: _cache[1] || (_cache[1] = ($event: any) => (insertComposerTokenAndBrowse('mention', '@'))),
          onLink: _cache[2] || (_cache[2] = ($event: any) => (insertComposerTokenAndBrowse('ncr', '#'))),
          onAttach: openAttachmentPicker,
          onUpload: openAttachmentPicker,
          onInput: updateComposerSuggestions,
          onSelectionChange: updateComposerSuggestions,
          onKeydown: handleComposerKeydown
        }, {
          attachments: _withCtx(() => [
            (_openBlock(true), _createElementBlock(_Fragment, null, _renderList(draftAttachments.value, (attachment) => {
              return (_openBlock(), _createElementBlock("div", {
                key: `draft-${attachment.fileId}`,
                class: _normalizeClass(['composer-attachment-card', `is-${attachment.previewKind}`])
              }, [
                (attachmentPreviewUrls.value[attachment.fileId])
                  ? (_openBlock(), _createElementBlock("img", {
                      key: 0,
                      src: attachmentPreviewUrls.value[attachment.fileId],
                      alt: `${attachment.displayName} preview`,
                      class: "composer-attachment-image",
                      loading: "lazy"
                    }, null, 8 /* PROPS */, _hoisted_17))
                  : (_openBlock(), _createElementBlock("span", _hoisted_18, _toDisplayString(attachmentLabel(attachment)), 1 /* TEXT */)),
                _createElementVNode("span", _hoisted_19, _toDisplayString(attachment.displayName), 1 /* TEXT */),
                _createElementVNode("button", {
                  type: "button",
                  class: "composer-attachment-remove",
                  "aria-label": `Remove ${attachment.displayName}`,
                  onClick: _withModifiers(($event: any) => (removeDraftAttachment(attachment.fileId)), ["stop"])
                }, [...(_cache[4] || (_cache[4] = [
                  _createElementVNode("i", { class: "material-icons" }, "close", -1 /* CACHED */)
                ]))], 8 /* PROPS */, _hoisted_20)
              ], 2 /* CLASS */))
            }), 128 /* KEYED_FRAGMENT */)),
            (isUploadingAttachments.value)
              ? (_openBlock(), _createElementBlock("div", _hoisted_21, [
                  _createElementVNode("span", _hoisted_22, _toDisplayString(uploadLabel.value), 1 /* TEXT */),
                  _createElementVNode("div", _hoisted_23, [
                    _createElementVNode("div", {
                      class: "composer-attachment-progress-bar",
                      style: _normalizeStyle({ width: `${uploadProgress.value}%` })
                    }, null, 4 /* STYLE */)
                  ])
                ]))
              : _createCommentVNode("v-if", true)
          ]),
          suggestions: _withCtx(() => [
            (composerSuggestions.value.length)
              ? (_openBlock(), _createElementBlock("div", _hoisted_24, [
                  (_openBlock(true), _createElementBlock(_Fragment, null, _renderList(composerSuggestions.value, (suggestion, index) => {
                    return (_openBlock(), _createElementBlock("button", {
                      key: suggestion.key,
                      type: "button",
                      class: _normalizeClass(['composer-suggestion-item', { active: index === highlightedSuggestionIndex.value }]),
                      onMouseenter: ($event: any) => (highlightedSuggestionIndex.value = index),
                      onMousedown: _withModifiers(($event: any) => (applyComposerSuggestion(index)), ["prevent"])
                    }, [
                      (suggestion.kind === 'mention')
                        ? (_openBlock(), _createElementBlock("img", {
                            key: 0,
                            src: suggestion.avatar,
                            class: "composer-suggestion-avatar",
                            alt: ""
                          }, null, 8 /* PROPS */, _hoisted_26))
                        : (_openBlock(), _createElementBlock("div", _hoisted_27, "#")),
                      _createElementVNode("div", _hoisted_28, [
                        _createElementVNode("span", _hoisted_29, _toDisplayString(suggestion.label), 1 /* TEXT */),
                        (suggestion.secondary)
                          ? (_openBlock(), _createElementBlock("span", _hoisted_30, "›"))
                          : _createCommentVNode("v-if", true),
                        (suggestion.secondary)
                          ? (_openBlock(), _createElementBlock("span", _hoisted_31, _toDisplayString(suggestion.secondary), 1 /* TEXT */))
                          : _createCommentVNode("v-if", true)
                      ])
                    ], 42 /* CLASS, PROPS, NEED_HYDRATION */, _hoisted_25))
                  }), 128 /* KEYED_FRAGMENT */))
                ]))
              : _createCommentVNode("v-if", true)
          ]),
          _: 1 /* STABLE */
        }, 8 /* PROPS */, ["modelValue", "avatar-url", "sending", "send-disabled", "can-attach", "has-attachments", "force-expanded", "highlight-html", "is-editing"]))
      : _createCommentVNode("v-if", true),
    _createElementVNode("input", {
      ref_key: "attachmentInputRef",
      ref: attachmentInputRef,
      type: "file",
      class: "ncr-files-hidden-input",
      multiple: "",
      onChange: handleAttachmentInputChange
    }, null, 544 /* NEED_HYDRATION, NEED_PATCH */)
  ]))
}
}

})